import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { PartySelector } from '@/app/components/common/PartySelector'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, AlertTriangle, TruckIcon } from 'lucide-react'
import { VoucherResponse } from '@/app/types/voucher'
import { DeliveryChallanResponse } from '@/app/types/deliveryChallan'
import { Badge } from '@/components/ui/badge'

// Define schema for the form
const deliveryChallanSchema = z.object({
  vehicle_number: z.string().optional(),
  transport_mode: z.string().optional(),
  eway_bill_number: z.string().optional(),
  assigned_to_party_id: z.number().optional().nullable(),
  notes: z.string().optional(),
  selectedItems: z.array(z.object({
    item_id: z.number(),
    item_name: z.string(),
    quantity: z.number().min(0).transform((val) => {
      // Round to 3 decimal places to handle floating point issues
      return Math.round(val * 1000) / 1000
    }),
    max_quantity: z.number(),
    selected: z.boolean(),
    voucher_item_id: z.number().optional(),
  })).refine(
    (items) => items.some((item) => item.selected && item.quantity > 0),
    { message: 'Please select at least one item with quantity to deliver.' }
  ),
})

type DeliveryChallanFormData = z.infer<typeof deliveryChallanSchema>

interface CreateDeliveryChallanModalProps {
  isOpen: boolean
  onClose: () => void
  salesInvoice: VoucherResponse
  existingDCs: DeliveryChallanResponse[]
  onCreateDC: (data: {
    vehicle_number?: string
    transport_mode?: string
    eway_bill_number?: string
    assigned_to_party_id?: number
    notes?: string
    items?: Array<{
      item_id: number
      quantity: number
      voucher_item_id?: number
    }>
  }) => Promise<void>
  isCreating: boolean
}

export const CreateDeliveryChallanModal: React.FC<CreateDeliveryChallanModalProps> = ({
  isOpen,
  onClose,
  salesInvoice,
  existingDCs,
  onCreateDC,
  isCreating,
}) => {
  const [selectedAll, setSelectedAll] = useState(false)
  const [inputValues, setInputValues] = useState<{ [key: number]: string }>({})  // Track input display values

  // Log the invoice and existing DCs when in development
  useEffect(() => {
    if (isOpen && process.env.NODE_ENV === 'development') {
      console.log('CreateDeliveryChallanModal opened for invoice:', salesInvoice.voucher_number, 'ID:', salesInvoice.id)
      console.log('Existing DCs:', existingDCs.length)
    }
  }, [isOpen, salesInvoice, existingDCs])

  // Calculate delivered quantities per voucher item from existing DCs
  const getDeliveredQuantity = (voucherItemId: number | undefined, itemId: number): number => {
    let delivered = 0
    
    // Only process DCs that are actually related to this invoice
    // existingDCs should only contain DCs for this specific invoice
    existingDCs.forEach((dc) => {
      // Ensure this DC is for the current invoice
      if (dc.related_invoice_voucher_id !== salesInvoice.id) {
        console.warn(`DC ${dc.id} is not related to current invoice ${salesInvoice.id}`)
        return
      }
      
      // First try to match by reference_voucher_item_id if available
      if (voucherItemId && dc.items) {
        const dcItem = dc.items.find((item) => item.reference_voucher_item_id === voucherItemId)
        if (dcItem) {
          delivered += Number(dcItem.quantity)
        }
      } else if (dc.items) {
        // Fallback: match by item_id (for legacy data without reference_voucher_item_id)
        const dcItem = dc.items.find((item) => item.item_id === itemId)
        if (dcItem) {
          delivered += Number(dcItem.quantity)
        }
      }
    })
    return delivered
  }

  // Prepare item data with remaining quantities
  const prepareItemData = (): DeliveryChallanFormData['selectedItems'] => {
    const items = (salesInvoice.items || []).map((item) => {
      const invoiceQty = Number(item.quantity)
      const deliveredQty = getDeliveredQuantity(item.id, item.item_id)
      const remainingQty = Math.max(0, invoiceQty - deliveredQty)
      
      
      return {
        item_id: item.item_id,
        item_name: item.name || item.item?.name || `Item ${item.item_id}`,
        quantity: remainingQty,
        max_quantity: remainingQty,
        selected: remainingQty > 0,
        voucher_item_id: item.id,
      }
    })
    
    // Sort items: undelivered items first, then delivered items
    return items.sort((a, b) => {
      // Items with remaining quantity come first
      if (a.max_quantity > 0 && b.max_quantity === 0) return -1
      if (a.max_quantity === 0 && b.max_quantity > 0) return 1
      // Within each group, maintain original order
      return 0
    })
  }

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<DeliveryChallanFormData>({
    resolver: zodResolver(deliveryChallanSchema),
    defaultValues: {
      vehicle_number: '',
      transport_mode: '',
      eway_bill_number: '',
      assigned_to_party_id: null,
      notes: `Delivery for Sales Invoice: ${salesInvoice.voucher_number}`,
      selectedItems: prepareItemData(),
    },
  })


  const selectedItems = watch('selectedItems')

  // Initialize input values when modal opens or items change
  useEffect(() => {
    if (isOpen) {
      const initialValues: { [key: number]: string } = {}
      selectedItems.forEach((item, index) => {
        initialValues[index] = item.quantity.toString()
      })
      setInputValues(initialValues)
    }
  }, [isOpen])

  // Handle select all functionality
  const handleSelectAll = (checked: boolean) => {
    setSelectedAll(checked)
    const updatedItems = selectedItems.map((item) => ({
      ...item,
      selected: checked && item.max_quantity > 0,
    }))
    setValue('selectedItems', updatedItems)
  }

  // Handle individual item selection
  const handleItemSelection = (index: number, checked: boolean) => {
    const updatedItems = [...selectedItems]
    updatedItems[index].selected = checked
    setValue('selectedItems', updatedItems)
    
    // Update select all checkbox state
    const allSelected = updatedItems
      .filter((item) => item.max_quantity > 0)
      .every((item) => item.selected)
    setSelectedAll(allSelected)
  }

  // Handle quantity change
  const handleQuantityChange = (index: number, value: string) => {
    // Prevent typing more than 2 decimal places
    if (value.includes('.')) {
      const parts = value.split('.')
      if (parts[1] && parts[1].length > 2) {
        return
      }
    }

    // Allow only valid decimal number input (including partial inputs like "0." or "2.")
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
      return
    }

    // Update the input display value
    setInputValues(prev => ({ ...prev, [index]: value }))

    // Update the actual numeric value
    const updatedItems = [...selectedItems]
    
    if (value === '') {
      updatedItems[index].quantity = 0
    } else if (value.endsWith('.') || value === '0') {
      // Keep these as-is for display, but store numeric value
      updatedItems[index].quantity = parseFloat(value) || 0
    } else {
      const numValue = parseFloat(value) || 0
      updatedItems[index].quantity = Math.min(numValue, updatedItems[index].max_quantity)
    }
    
    setValue('selectedItems', updatedItems)
  }

  // Form submission
  const onSubmit = async (data: DeliveryChallanFormData) => {
    // Filter selected items with quantity > 0
    const itemsToDeliver = data.selectedItems
      .filter((item) => item.selected && item.quantity > 0)
      .map((item) => ({
        item_id: item.item_id,
        quantity: item.quantity,
        voucher_item_id: item.voucher_item_id,
      }))

    if (itemsToDeliver.length === 0) {
      // This shouldn't happen due to schema validation, but just in case
      console.error('No items selected for delivery')
      return
    }

    await onCreateDC({
      vehicle_number: data.vehicle_number,
      transport_mode: data.transport_mode,
      eway_bill_number: data.eway_bill_number,
      assigned_to_party_id: data.assigned_to_party_id ?? undefined,
      notes: data.notes,
      items: itemsToDeliver,
    })
  }

  // Check if there are any items left to deliver
  const hasDeliverableItems = selectedItems.some((item) => item.max_quantity > 0)
  const hasSelectedItems = selectedItems.some((item) => item.selected && item.quantity > 0)

  const handleClose = () => {
    setInputValues({})  // Clear input values when closing
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Delivery Challan</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* DC Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="vehicle_number">Vehicle Number</Label>
              <Input
                id="vehicle_number"
                placeholder="e.g., KA01AB1234"
                {...register('vehicle_number')}
              />
            </div>
            <div>
              <Label htmlFor="transport_mode">Transport Mode</Label>
              <Input
                id="transport_mode"
                placeholder="e.g., Road, Rail, Air"
                {...register('transport_mode')}
              />
            </div>
            <div>
              <Label htmlFor="eway_bill_number">E-Way Bill Number</Label>
              <Input
                id="eway_bill_number"
                placeholder="e.g., 123456789012"
                {...register('eway_bill_number')}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="assigned_to_party_id">Assigned Field Agent</Label>
            <PartySelector
              value={watch('assigned_to_party_id') ?? undefined}
              onChange={(value) => setValue('assigned_to_party_id', value ?? null)}
              placeholder="Select field agent..."
              filterPartyGroup="EMPLOYEE"
              disabled={isCreating}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Assign a field agent to handle this delivery
            </p>
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              rows={2}
              {...register('notes')}
            />
          </div>

          {/* Items Selection */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <Label>Select Items to Deliver</Label>
              {existingDCs.length > 0 && (
                <Badge variant="outline">
                  {existingDCs.length} existing DC{existingDCs.length > 1 ? 's' : ''}
                </Badge>
              )}
            </div>

            {!hasDeliverableItems ? (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  All items from this invoice have been fully delivered.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedAll}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Invoice Qty</TableHead>
                      <TableHead className="text-right">Delivered</TableHead>
                      <TableHead className="text-right">Remaining</TableHead>
                      <TableHead className="text-right">Qty to Deliver</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedItems.map((item, index) => {
                      // Find the corresponding invoice item by voucher_item_id or use index as fallback
                      const invoiceItem = item.voucher_item_id 
                        ? salesInvoice.items?.find((i) => i.id === item.voucher_item_id)
                        : salesInvoice.items?.[index]
                      
                      const invoiceQty = Number(invoiceItem?.quantity || 0)
                      const deliveredQty = invoiceQty - item.max_quantity

                      const isFullyDelivered = item.max_quantity === 0
                      
                      // Check if this is the first delivered item (to add a separator)
                      const isFirstDeliveredItem = isFullyDelivered && 
                        (index === 0 || selectedItems[index - 1].max_quantity > 0)
                      
                      return (
                        <>
                          {isFirstDeliveredItem && (
                            <TableRow>
                              <TableCell colSpan={6} className="h-8 text-center text-xs text-muted-foreground bg-muted/20">
                                — Fully Delivered Items —
                              </TableCell>
                            </TableRow>
                          )}
                        <TableRow 
                          key={`${item.voucher_item_id || index}`}
                          className={isFullyDelivered ? 'opacity-60 bg-muted/30' : ''}
                        >
                          <TableCell>
                            <Checkbox
                              checked={item.selected}
                              onCheckedChange={(checked) =>
                                handleItemSelection(index, checked as boolean)
                              }
                              disabled={isFullyDelivered}
                            />
                          </TableCell>
                          <TableCell>
                            <span className={isFullyDelivered ? 'line-through text-muted-foreground' : ''}>
                              {item.item_name}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">{invoiceQty}</TableCell>
                          <TableCell className="text-right">{deliveredQty}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant={isFullyDelivered ? 'secondary' : 'default'}>
                              {isFullyDelivered ? 'Delivered' : item.max_quantity}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {isFullyDelivered ? (
                              <span className="text-sm text-muted-foreground">-</span>
                            ) : (
                              <Input
                                type="text"
                                value={inputValues[index] !== undefined ? inputValues[index] : item.quantity}
                                onChange={(e) => handleQuantityChange(index, e.target.value)}
                                disabled={!item.selected}
                                className="w-24 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                placeholder="0"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                        </>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {errors.selectedItems && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Please select at least one item with quantity to deliver.
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isCreating}
              >
                Cancel
              </Button>
              {hasDeliverableItems && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    // Create full DC with all remaining items
                    const formData = watch()
                    onCreateDC({
                      vehicle_number: formData.vehicle_number,
                      transport_mode: formData.transport_mode,
                      eway_bill_number: formData.eway_bill_number,
                      assigned_to_party_id: formData.assigned_to_party_id ?? undefined,
                      notes: formData.notes,
                      // Omit items to create full DC
                    })
                  }}
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <TruckIcon className="h-4 w-4 mr-2" />
                  )}
                  Deliver All Remaining
                </Button>
              )}
            </div>
            <Button
              type="submit"
              disabled={isCreating || !hasSelectedItems || !hasDeliverableItems}
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <TruckIcon className="h-4 w-4 mr-2" />
                  Create Partial DC
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}